// ═══════════════════════════════════════
// AUTOCOMPLETE — Dropdown ornements ABC
// ═══════════════════════════════════════

import { getContextAtCursor, applyEdit, deleteSelection, getAbcContent, saveCursor } from './editor.js';
import { historyPushNow } from './history.js';
import { markDirty } from './autosave.js';
import { render } from './renderer.js';
import { onInstrumentsChange, hasInstrument } from './instrument-context.js';

// ── Transposition écossais → breton ───────────────────────────────────────────

const SCOTS_TO_BRETON_NOTE = {
    'G': 'A,', 'A': 'B,', 'B': 'C', 'c': 'D',
    'd': 'E',  'e': 'F',  'f': 'G', 'g': 'A', 'a': 'B'
};

const SCOTS_TO_BRETON_KEY = {
    'G': 'A,', 'A': 'B,', 'B': 'C', 'c': 'D',
    'd': 'E',  'e': 'F',  'f': 'G', 'g': 'A', 'a': 'B'
};

function _isBreton() {
    // Le K: est dans les éléments #abc-voice-header-N (éditeur multi-voix),
    // et non dans #abc qui est l'élément legacy non synchronisé.
    const headers = [...document.querySelectorAll('[id^="abc-voice-header-"]')]
        .map(el => el.textContent)
        .join('\n');
    return /^K:\s*Cm(?:in)?/im.test(headers);
}

function _transposeAbcString(abcStr) {
    // Transpose chaque note grace dans {xxx}
    // On remplace lettre par lettre dans l'ordre du plus long au plus court
    // pour éviter de re-remplacer (ex: A, ne doit pas être re-traité)
    return abcStr.replace(/\{([^}]+)\}/g, (match, inner) => {
        let result = '';
        let i = 0;
        while (i < inner.length) {
            const ch = inner[i];
            if (SCOTS_TO_BRETON_NOTE[ch] !== undefined) {
                result += SCOTS_TO_BRETON_NOTE[ch];
            } else {
                result += ch;
            }
            i++;
        }
        return '{' + result + '}';
    });
}

function _getActiveMap() {
    if (!_isBreton()) return ORNAMENTS_MAP;

    const transposed = {};
    for (const [key, items] of Object.entries(ORNAMENTS_MAP)) {
        const newKey = SCOTS_TO_BRETON_KEY[key] ?? key;
        transposed[newKey] = items.map(item => ({
            ...item,
            abc: _transposeAbcString(item.abc)
        }));
    }
    return transposed;
}

const ORNAMENTS_MAP = {

    'G':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Mi',   abc: '{d}',   desc: ''},
        { label: 'Détaché de Fa',   abc: '{e}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Doublé de LA',   abc: '{gGd}',   desc: ''},
        { label: 'Doublé de LA',   abc: '{aGd}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de LA',   abc: '{Gd}',   desc: 'suit un Si aigu'},
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},
    ],
    'A':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Mi',   abc: '{d}',   desc: ''},
        { label: 'Détaché de Fa',   abc: '{e}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'battement de La grave',   abc: '{G}',   desc: ''},
        { label: 'Doublé de Si',   abc: '{gAd}',   desc: ''},
        { label: 'Doublé de Si',   abc: '{aAd}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de Si',   abc: '{Ad}',   desc: 'suit un Si aigu'},
        { label: 'Birl',   abc: '{GAG}',   desc: ''},
        { label: 'Birl en écho',   abc: '{AGAG}',   desc: ''},
        { label: 'Birl avec détaché',   abc: '{gAGAG}',   desc: ''},
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},
        { label: 'Torluath',   abc: '{GdGe}',   desc: ''},
        { label: 'Torluath',   abc: '{GBGe}',   desc: 'Suit un Mi'},
    ],
    'B':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Mi',   abc: '{d}',   desc: ''},
        { label: 'Détaché de Fa',   abc: '{e}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Battement de La grave',   abc: '{G}',   desc: ''},
        { label: 'Slur',   abc: '{gBG}',   desc: ''},
        { label: 'Slur',   abc: '{aBG}',   desc: 'Suit un La aigu'},
        { label: 'Slur',   abc: '{BG}',   desc: 'Suit un Si aigu'},
        { label: 'Doublé de Do',   abc: '{gBd}',   desc: ''},
        { label: 'Doublé de Do',   abc: '{aBd}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de Do',   abc: '{Bd}',   desc: 'suit un Si aigu'},  
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'}, 
        { label: 'Torluath',   abc: '{GdGe}',   desc: ''},
        { label: 'Torluath',   abc: '{GBGe}',   desc: 'Suit un Mi'},     
    ],
    'c':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Mi',   abc: '{d}',   desc: ''},
        { label: 'Détaché de Fa',   abc: '{e}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Battement de La grave',   abc: '{G}',   desc: ''},
        { label: 'Slur',   abc: '{gcG}',   desc: ''},
        { label: 'Slur',   abc: '{acG}',   desc: 'Suit un La aigu'},
        { label: 'Slur',   abc: '{cG}',   desc: 'Suit un Si aigu'},
        { label: 'Doublé de Ré',   abc: '{gcd}',   desc: ''},
        { label: 'Doublé de Ré',   abc: '{acd}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de Ré',   abc: '{cd}',   desc: 'suit un Si aigu'},   
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'}, 
        { label: 'Torluath',   abc: '{GdGe}',   desc: ''},
        { label: 'Torluath',   abc: '{GBGe}',   desc: 'Suit un Mi'},    
    ],
    'd':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Fa',   abc: '{e}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Battement de Ré',   abc: '{c}',   desc: ''},
        { label: 'Battement de La grave',   abc: '{G}',   desc: ''},
        { label: 'Slur lourd',   abc: '{gdG}',   desc: ''},
        { label: 'Slur lourd',   abc: '{adG}',   desc: 'Suit un La aigu'},
        { label: 'Slur lourd',   abc: '{dG}',   desc: 'Suit un Si aigu'},
        { label: 'Slur léger',   abc: '{gdc}',   desc: ''},
        { label: 'Slur léger',   abc: '{adc}',   desc: 'Suit un La aigu'},
        { label: 'Slur léger',   abc: '{dc}',   desc: 'Suit un Si aigu'},
        { label: 'Doublé de Mi',   abc: '{gde}',   desc: ''},
        { label: 'Doublé de Mi',   abc: '{ade}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de Mi',   abc: '{de}',   desc: 'suit un Si aigu'},         
        { label: 'Throw on D',   abc: '{Gdc}',   desc: 'ou doublé de mi inférieur'},         
        { label: 'Throw on D lourd',   abc: '{GdGc}',   desc: 'ou doublé de mi inférieur lourd'}, 
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},        
    ],
    'e':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Battement de Si grave',   abc: '{A}',   desc: ''},
        { label: 'Doublé de Fa',   abc: '{gef}',   desc: ''},
        { label: 'Doublé de Fa',   abc: '{aef}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de Fa',   abc: '{ef}',   desc: 'suit un Si aigu'},        
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},
    ],
    'f':[
        { label: 'Détaché de La',   abc: '{g}',   desc: ''},
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Battement de Fa',   abc: '{e}',   desc: ''},
        { label: 'Doublé de Sol',   abc: '{gfg}',   desc: ''},
        { label: 'Doublé de Sol',   abc: '{afg}',   desc: 'suit un La aigu'},
        { label: 'Demi-doublé de Sol',   abc: '{fg}',   desc: 'suit un Si aigu'}, 
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},         
    ],
    'g':[
        { label: 'Détaché de Si aigu',   abc: '{a}',   desc: ''},
        { label: 'Battement de Sol',   abc: '{f}',   desc: ''},
        { label: 'Demi-doublé de La aigu',   abc: '{gf}',   desc: ''},      
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},   
    ],
    'a':[
        { label: 'Battement de La',   abc: '{g}',   desc: ''},
        { label: 'Demi-doublé de Si aigu',   abc: '{ag}',   desc: ''}, 
        { label: 'Leumluath',   abc: '{GdG}',   desc: 'ou Grip'},
        { label: 'Leumluath',   abc: '{GBG}',   desc: 'ou Grip, suit un Mi'},
    ],
}
/*
const ORNAMENTS_FALLBACK = [
    { label: 'Grace G', abc: '{G}',   desc: 'Grace note G (universel)' },
    { label: 'Grip',    abc: '{GdG}', desc: 'Grip standard' },
    { label: 'Throw D', abc: '{GdC}', desc: 'Throw / frowandy' },
];*/
const ORNAMENTS_FALLBACK = [

];

// ── Caisse claire — palette d'ornements ───────────────────────────────────────
// Pas de déclencheur clavier unique en caisse claire (lettre, {, !, …) :
// la palette s'ouvre via le bouton flottant #abc-orn-btn ou Ctrl+Espace.
// Chaque item insère `before` au curseur ; si `after` est défini et qu'une
// sélection existe, la sélection est encadrée (ex. crescendo).
// {autre} = l'autre main que celle de la note qui suit le curseur.

const SNARE_INSTRUMENT = 'snare-drums';

const SNARE_PALETTE = [
    { group: 'Agréments',  label: 'Flam',               before: '{{autre}}',     desc: 'Agrément de l\'autre main' },
    { group: 'Agréments',  label: 'Drag',               before: '{{autre}{autre}}', desc: '2 agréments de l\'autre main' },
    { group: 'Roulements', label: 'Roulement 1 barre',  before: '!/!',           desc: 'Avant la note' },
    { group: 'Roulements', label: 'Roulement 2 barres', before: '!//!',          desc: 'Ajouter - après la note pour lier' },
    { group: 'Roulements', label: 'Roulement 3 barres', before: '!///!',         desc: 'Roulement serré' },
    { group: 'Nuances',    label: 'Accent',             before: '!>!',           desc: 'Avant la note' },
    { group: 'Nuances',    label: 'Crescendo',          before: '!<(!',        after: '!<)!',        desc: 'Encadre la sélection' },
    { group: 'Nuances',    label: 'Decrescendo',        before: '!>(!',        after: '!>)!',        desc: 'Encadre la sélection' },
    { group: 'Nuances',    label: 'Triolet',            before: '(3',            desc: 'Avant les 3 notes' },
    { group: 'Signes',     label: 'Archet vers le haut', before: 'u',            desc: 'ou !upbow! — juste avant la note' },
    { group: 'Signes',     label: 'Archet vers le bas',  before: 'v',            desc: 'ou !downbow! — juste avant la note' },
];

let _acItems       = [];
let _acActiveIdx   = 0;
let _acAnchor      = null;     // Range cloné ou élément (getBoundingClientRect)
let _acNote        = null;
let _acMode        = 'brace';  // 'brace' (déclenché par {) | 'palette' (caisse claire)
let _lastVoiceEl   = null;

// Getter exporté pour main.js
export function acActiveIdx() { return _acActiveIdx; }

export function acIsDropdownOpen() {
    const dd = document.getElementById('ornament-dropdown');
    return !!(dd && dd.style.display === 'block');
}

function _acIsHeaderLine(before) {
    return /^[A-Za-z]:/.test(before.split('\n').pop());
}

function _voiceFromSelection() {
    const sel = window.getSelection();
    if (!sel.rangeCount) return null;
    const range = sel.getRangeAt(0);
    return range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement?.closest('.abc-voice')
        : range.startContainer?.closest?.('.abc-voice');
}

export function acGetContext() {
    const el = _voiceFromSelection();
    if (!el) return null;
    return getContextAtCursor(el);
}


export function acOpen(range, note) {
    _acMode   = 'brace';
    _acNote   = note;
    _acAnchor = range.cloneRange();

    const activeMap = _getActiveMap();
    _acItems = (note && activeMap[note]) ? activeMap[note] : ORNAMENTS_FALLBACK;

    _acActiveIdx    = 0;
    _acRender();
    _acPosition(_acAnchor);
    document.getElementById('ornament-dropdown').style.display = 'block';
}

export function acClose() {
    document.getElementById('ornament-dropdown').style.display = 'none';
    _acAnchor = null;
}

function _acRender() {
    const dd  = document.getElementById('ornament-dropdown');
    const lbl = _acMode === 'palette'
        ? `Ornements caisse claire${_acNote ? ` — note suivante <b>${_acNote}</b>` : ''}`
        : (_acNote ? `Ornements pour <b>${_acNote}</b>` : 'Ornements généraux');

    let lastGroup = null;
    dd.innerHTML = `<div class="ac-header">${lbl}</div><div class="ac-list">` +
        _acItems.map((it, i) => {
            let groupHtml = '';
            if (it.group && it.group !== lastGroup) {
                groupHtml = `<div class="ac-group">${it.group}</div>`;
                lastGroup = it.group;
            }
            const shown = it.abc ?? (it.before + (it.after ? '…' + it.after : ''));
            return groupHtml + `
            <div class="ac-item${i === _acActiveIdx ? ' active' : ''}" data-i="${i}">
                <span class="ac-abc">${shown}</span>
                <span class="ac-info">
                    <span class="ac-label">${it.label}</span>
                    <span class="ac-desc">${it.desc}</span>
                </span>
            </div>`;
        }).join('') +
        `</div>`;
    dd.querySelectorAll('.ac-item').forEach(el => {
        el.addEventListener('mousedown', e => { e.preventDefault(); acInsert(parseInt(el.dataset.i)); });
    });
}

function _acPosition(anchor) {
    const dd   = document.getElementById('ornament-dropdown');
    const rect = anchor.getBoundingClientRect();
    dd.style.top  = (rect.bottom + window.scrollY + 4) + 'px';
    dd.style.left = (rect.left   + window.scrollX)     + 'px';
    requestAnimationFrame(() => {
        const r       = dd.getBoundingClientRect();
        const vw      = window.innerWidth;
        const vh      = window.innerHeight;
        const margin  = 8;

        // Débordement à droite
        if (r.right > vw - margin) {
            const newLeft = Math.max(margin, vw - r.width - margin);
            dd.style.left = (newLeft + window.scrollX) + 'px';
        }
        // Débordement en bas → afficher au-dessus de l'ancre
        if (r.bottom > vh - margin) {
            dd.style.top = (rect.top + window.scrollY - r.height - 4) + 'px';
        }
        // Débordement à gauche (après correction droite)
        const r2 = dd.getBoundingClientRect();
        if (r2.left < margin) {
            dd.style.left = (margin + window.scrollX) + 'px';
        }
    });
}

export function acSetActive(i) {
    _acActiveIdx = Math.max(0, Math.min(_acItems.length - 1, i));
    _acRender();
    if (_acAnchor) _acPosition(_acAnchor);
}

export function acInsert(idx) {
    const item = _acItems[idx];
    if (!item) return;
    const el = _voiceFromSelection();
    if (!el) return;
    const mode = _acMode;
    acClose();

    if (mode === 'brace') {
        applyEdit(el, ({ lines, lineIdx, col }) => {
            const next = [...lines];
            next[lineIdx] = next[lineIdx].slice(0, col - 1) + item.abc + next[lineIdx].slice(col);
            return { lines: next, lineIdx, col: col - 1 + item.abc.length };
        });
    } else {
        const before = item.before;
        const after  = item.after ?? '';
        applyEdit(el, (state) => {
            const { lineIdx, col, endLineIdx, endCol, collapsed } = state;
            // Sélection + item encadrant → on entoure la sélection
            if (!collapsed && after) {
                const next = [...state.lines];
                next[endLineIdx] = next[endLineIdx].slice(0, endCol) + after + next[endLineIdx].slice(endCol);
                next[lineIdx]    = next[lineIdx].slice(0, col) + before + next[lineIdx].slice(col);
                const shift = (lineIdx === endLineIdx) ? before.length : 0;
                return { lines: next, lineIdx: endLineIdx, col: endCol + shift + after.length };
            }
            // Sinon : insertion au curseur, curseur placé entre before et after
            const base = collapsed ? state : deleteSelection(state);
            const next = [...base.lines];
            next[base.lineIdx] = next[base.lineIdx].slice(0, base.col) + before + after + next[base.lineIdx].slice(base.col);
            return { lines: next, lineIdx: base.lineIdx, col: base.col + before.length };
        });
    }

    historyPushNow(getAbcContent(), saveCursor(el));
    render();
    markDirty();
}

export function acCheckTrigger(ctx) {
    // Toute frappe referme la palette caisse claire
    if (_acMode === 'palette') acClose();
    if (!ctx) return;
    if (!hasInstrument('bagpipe')) return;
    if (ctx.before.endsWith('{') && !_acIsHeaderLine(ctx.before)) {
        const m = ctx.after.match(/^([A-Ga-g],?)/);
        acOpen(ctx.range, m ? m[1] : null);
    } else {
        acClose();
    }
}

// ── Palette caisse claire ─────────────────────────────────────────────────────

export function acCanOpenPalette() {
    return hasInstrument(SNARE_INSTRUMENT);
}

/**
 * Ouvre la palette caisse claire.
 * @param {Element|null} anchorEl — élément sous lequel afficher la palette
 *                                  (null → sous le curseur)
 */
export function acOpenPalette(anchorEl = null) {
    if (!acCanOpenPalette()) return;

    // S'assurer qu'un curseur est dans une voix (sinon : dernière voix utilisée, en fin)
    if (!_voiceFromSelection()) {
        const el = _lastVoiceEl?.isConnected
            ? _lastVoiceEl
            : document.querySelector('.abc-voice[contenteditable]');
        if (!el) return;
        el.focus();
        const r = document.createRange();
        r.selectNodeContents(el);
        r.collapse(false);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
    }

    const ctx = acGetContext();
    if (!ctx) return;

    // Note qui suit le curseur (en sautant décorations / agréments déjà présents)
    const m = ctx.after.match(/^(?:\{[^}]*\}|![^!\n]*!|[.~uvHLMOPST>\s])*([A-Ga-g])/);
    _acNote = m ? m[1] : null;
    const main  = _acNote ?? 'c';
    const autre = main === 'A' ? 'c' : 'A';

    _acItems = SNARE_PALETTE.map(it => ({
        ...it,
        before: it.before.replaceAll('{autre}', autre),
    }));
    _acMode      = 'palette';
    _acActiveIdx = 0;
    _acAnchor    = anchorEl ?? ctx.range;
    _acRender();
    _acPosition(_acAnchor);
    document.getElementById('ornament-dropdown').style.display = 'block';
}

export function initSnarePalette() {
    const btn = document.getElementById('abc-orn-btn');

    // Mémoriser la dernière voix éditée
    document.getElementById('voices-container')?.addEventListener('focusin', (e) => {
        const v = e.target.closest?.('.abc-voice[contenteditable]');
        if (v) _lastVoiceEl = v;
    });

    if (!btn) return;
    const refresh = () => {
        btn.hidden = !acCanOpenPalette();
        if (btn.hidden && _acMode === 'palette') acClose();
    };
    refresh();
    onInstrumentsChange(refresh);

    // mousedown : garder le focus/la sélection dans la voix
    let wasOpen = false;
    btn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        wasOpen = acIsDropdownOpen() && _acMode === 'palette';
    });
    btn.addEventListener('click', () => {
        if (wasOpen) { acClose(); wasOpen = false; return; }
        acOpenPalette(btn);
    });
}

// Ferme le dropdown cornemuse si la cornemuse disparaît
onInstrumentsChange(() => {
    if (!hasInstrument('bagpipe') && _acMode === 'brace') acClose();
});
